import { useEffect, useState } from 'react';
import { listLedgerAccounts } from '../../api/adminFinanceApi';

const ownerTypes = ['', 'PLATFORM', 'HOST', 'GUEST', 'NONE'];

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const AdminLedgerAccounts = ({ token }) => {
  const [filters, setFilters] = useState({ ownerType: '', ownerId: '', currency: '' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listLedgerAccounts(token, filters);
      setItems(res.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load ledger accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Ledger Accounts</h1>
          <p className="text-sm text-slate-600">Chart of accounts for platform, hosts, and guests.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <select
          value={filters.ownerType}
          onChange={(e) => setFilters((f) => ({ ...f, ownerType: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {ownerTypes.map((opt) => (
            <option key={opt || 'all'} value={opt}>
              {opt || 'All owners'}
            </option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Owner ID"
          value={filters.ownerId}
          onChange={(e) => setFilters((f) => ({ ...f, ownerId: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="text"
          placeholder="Currency (e.g. INR)"
          value={filters.currency}
          onChange={(e) => setFilters((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={load}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Owner</th>
              <th className="px-4 py-3">Currency</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={5} className="px-4 py-3 text-slate-500">
                  No accounts found.
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{row.code}</td>
                <td className="px-4 py-3 text-slate-700">{row.name}</td>
                <td className="px-4 py-3 text-slate-600">
                  {row.ownerType || row.owner_type || 'NONE'}
                  {row.ownerId || row.owner_id ? ` #${row.ownerId || row.owner_id}` : ''}
                </td>
                <td className="px-4 py-3 text-slate-700">{row.currency || 'INR'}</td>
                <td className="px-4 py-3 text-slate-500">{formatDateTime(row.createdAt || row.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminLedgerAccounts;
